import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useApiHelper from "../hooks/useApiHelper";
import {
  ICreateUpdateTemplateRequest,
  IProductDescriptionPreview,
  IProductDescriptionPreviewRequest,
  ITemplateEditorParentTemplate,
  ITemplateEditorResponse,
  ITemplateEntity,
} from "../models/TemplateEditor";
import { DefaultResponseWithData } from "../models/system";

export default function useTemplateEditor() {
  const { get, post, put, del } = useApiHelper();
  const queryClient = useQueryClient();

  const templateEditor = useQuery({
    queryKey: ["templateEditor"],
    queryFn: () => get<ITemplateEditorResponse>("/Template/editor"),
    staleTime: 1000 * 60 * 10,
  });

  const createChildTemplate = useMutation({
    mutationFn: ({ parentTemplateId, request }: { parentTemplateId: string; request: ICreateUpdateTemplateRequest }) =>
      post<ICreateUpdateTemplateRequest, DefaultResponseWithData<ITemplateEntity>>(`/Template/${parentTemplateId}/child`, request),
    onSuccess: () => {
      queryClient.invalidateQueries(["templateEditor"]);
    },
  });

  const updateChildTemplate = useMutation({
    mutationFn: ({ childTemplateId, request }: { childTemplateId: string; request: ICreateUpdateTemplateRequest }) =>
      put<ICreateUpdateTemplateRequest, DefaultResponseWithData<ITemplateEntity>>(`/Template/child/${childTemplateId}`, request),
    onSuccess: () => {
      queryClient.invalidateQueries(["templateEditor"]);
    },
  });

  const resetChildTemplate = useMutation({
    mutationFn: (childTemplateId: string) =>
      post<object, DefaultResponseWithData<ITemplateEntity>>(`/Template/child/${childTemplateId}/reset`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries(["templateEditor"]);
    },
  });

  const deleteChildTemplate = useMutation({
    mutationFn: (childTemplateId: string) =>
      del<DefaultResponseWithData<ITemplateEntity>>(`/Template/child/${childTemplateId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(["templateEditor"]);
    },
  });

  const previewProductDescription = useMutation({
    mutationFn: (request: IProductDescriptionPreviewRequest) =>
      post<IProductDescriptionPreviewRequest, DefaultResponseWithData<IProductDescriptionPreview>>("/Template/product-description/preview", request),
  });

  const templates = templateEditor.data?.data ?? [];
  const availableParents = templates.filter((template: ITemplateEditorParentTemplate) => !template.childTemplate);

  return {
    templateEditor,
    templates,
    availableParents,
    createChildTemplate,
    updateChildTemplate,
    resetChildTemplate,
    deleteChildTemplate,
    previewProductDescription,
  };
}
